import styled, { css, keyframes } from 'styled-components';

export const Container = styled.div`
    align-items: center;
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    padding: 12px;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 10000;
`;

export const Backdrop = styled.div`
    background-color: rgba(0, 0, 0, 0.5);
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: -1;
`;

export const Content = styled.div`
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
    max-height: calc(100dvh - 24px);
    max-width: 350px;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 16px;
    position: relative;
    -webkit-overflow-scrolling: touch;
    width: 100%;
`;

export const Form = styled.form<AuthModalFormProps>`
    display: flex;
    flex-direction: column;
    gap: 12px;

    ${({ $loading }) =>
        $loading &&
        css`
            opacity: 0.5;
            pointer-events: none;
            user-select: none;
        `}
`;

export const CloseButton = styled.button.attrs({ type: 'button' })`
    background-color: rgba(0, 0, 0, 0);
    border-radius: 100%;
    border: 0;
    color: #c7c7c7;
    height: 28px;
    line-height: 1;
    padding: 0;
    position: absolute;
    right: 7px;
    top: 7px;
    transition: background-color 0.2s ease-in-out;
    width: 28px;
    z-index: 1;

    :active,
    :focus,
    :hover {
        :not(:disabled) {
            background-color: rgba(0, 0, 0, 0.04);
        }
    }
`;

export const Row = styled.div`
    align-items: center;
    display: flex;
    gap: 8px;
    justify-content: center;
`;

export const Title = styled.div`
    color: #171716;
    font-size: 44px;
    font-weight: 700;
    line-height: 0.95;
    text-align: center;

    @media (max-width: 576px) {
        font-size: 36px;
    }
`;

export const Text = styled.p<AuthModalTextProps>`
    color: ${({ $error }) => ($error ? '#d32f2f' : '#171716')};
    font-size: ${({ $size }) => $size || 14}px;
    margin: 0;
    text-align: ${({ $center }) => ($center ? 'center' : 'inherit')};
`;

export const TextField = styled.input.attrs({ required: true })`
    font-size: 16px;
    border-radius: 8px;
    border: 1px solid #d8d8d8;
    line-height: 1.333;
    margin: 0;
    padding: 12px 14px;

    :active,
    :focus,
    :hover {
        border-color: #171716;
        outline: 0;
    }
`;

export const CheckboxWrapper = styled.div`
    align-items: center;
    cursor: pointer;
    display: flex;
    gap: 8px;

    > input {
        cursor: pointer;
    }

    > label {
        color: #171716;
        cursor: pointer;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
    }
`;

export const ModalDivider = styled.div`
    align-items: center;
    color: #5d5d5d;
    display: flex;
    font-size: 12px;
    gap: 12px;
    justify-content: center;
    line-height: 1;
    width: 100%;

    :before,
    :after {
        background-color: #d8d8d8;
        content: '';
        flex: 1;
        height: 1px;
    }
`;

export const OAuthButton = styled.button`
    align-items: center;
    background-color: #fff;
    border-radius: 8px;
    border: 2px solid #171716;
    color: #171716;
    cursor: pointer;
    display: inline-flex;
    font-family: Roboto, sans-serif;
    font-size: 18px;
    font-weight: 700;
    gap: 10px;
    height: 48px;
    justify-content: center;
    line-height: 1;
    padding: 0 14px;
    width: 100%;

    :disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

export const PrimaryButton = styled.button<{
    $tone?: 'dark' | 'yellow';
}>`
    align-items: center;
    background-color: ${({ $tone }) => ($tone === 'yellow' ? '#ffb100' : '#171716')};
    border-radius: 999px;
    border: 2px solid ${({ $tone }) => ($tone === 'yellow' ? '#ffb100' : '#171716')};
    color: ${({ $tone }) => ($tone === 'yellow' ? '#171716' : '#fff')};
    cursor: pointer;
    display: inline-flex;
    font-family: Roboto, sans-serif;
    font-size: 28px;
    font-weight: 700;
    height: 54px;
    justify-content: center;
    line-height: 1;
    padding: 0 20px;
    text-transform: none;
    width: 100%;

    :disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    @media (max-width: 576px) {
        font-size: 24px;
    }
`;

export const AuthSwitchButton = styled.button`
    background-color: transparent;
    border: 0;
    color: #171716;
    cursor: pointer;
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    padding: 0;
    position: relative;

    :after {
        background-image: linear-gradient(transparent 60%, #f6b32a 60%);
        bottom: -2px;
        content: '';
        height: 6px;
        left: -2px;
        position: absolute;
        right: -2px;
    }
`;

export const OAuthIconWrap = styled.span`
    display: inline-flex;
    height: 20px;
    width: 20px;
`;

const loaderAnimation = keyframes`
    0% {
        transform: translate(-50%, -50%) rotate(0deg);
    }
    100% {
        transform: translate(-50%, -50%) rotate(360deg);
    }
`;

const loaderCircleAnimation = keyframes`
    0% {
        stroke-dasharray: 1px, 200px;
        stroke-dashoffset: 0;
    }
    50% {
        stroke-dasharray: 100px, 200px;
        stroke-dashoffset: -15px;
    }
    100% {
        stroke-dasharray: 100px, 200px;
        stroke-dashoffset: -125px;
    }
`;

export const Loader = styled.svg`
    animation: ${loaderAnimation} 1.4s linear infinite;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
`;

export const LoaderCircle = styled.circle`
    animation: ${loaderCircleAnimation} 2s linear infinite;
    fill: none;
    stroke-dasharray: 80px, 200px;
    stroke-dashoffset: 0;
    stroke: currentColor;
    stroke-width: 4px;
`;

export const Notification = styled.div<AuthModalNotificationProps>`
    background-color: #43a048;
    border-radius: 8px;
    bottom: 0;
    color: #fff;
    left: 4px;
    max-width: 300px;
    padding: 12px 16px 12px 48px;
    position: absolute;
    transform: translateY(${({ $show }) => ($show ? 0 : 100)}%);
    transition: transform 0.3s ease-in-out;
    width: calc(100% - 8px);

    :before {
        background-color: #fff;
        border-radius: 100%;
        color: #43a048;
        content: '✓';
        font-weight: 700;
        height: 20px;
        left: 16px;
        line-height: 1;
        margin-right: 8px;
        padding: 1px;
        position: absolute;
        text-align: center;
        top: 14px;
        width: 20px;
    }
`;
